// Task operations with undo (invariant 8). Everything here is local to MeeMail: done, drop and
// snooze reach the server at once and Undo reopens the task; capturing from a card adds a task
// and a `task_ref` block, and Undo drops the task. Nothing here touches Gmail.
import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { CardView, TaskCreateRequest, TaskView, ThreadStatusView } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { deferWithUndo } from "@web/lib/deferredUndo";
import { feedApi } from "@web/lib/feedApi";
import { acceptCard, invalidateBoards } from "@web/lib/feedQueries";
import { tasksApi, type CaptureRequest } from "@web/lib/tasksApi";
import {
  acceptTask,
  invalidateReplies,
  invalidateTasks,
  removeTaskLocally,
  taskKeys,
} from "@web/lib/tasksQueries";
import { registerUndo } from "@web/lib/undoRegistry";
import { TASKS_TAB_LABEL } from "./tasksSearch";
import { newTaskRef } from "./taskRefs";
import { businessDaysLabel, short, taskViewFor } from "./wording";

function refused(error: unknown, fallback: string): string {
  if (isApiError(error, "invalid_request") && error.serverMessage) return error.serverMessage;
  return fallback;
}

async function reopen(queryClient: QueryClient, taskId: string): Promise<void> {
  acceptTask(queryClient, await tasksApi.setStatus(taskId, "reopen"));
}

export async function createTask(
  queryClient: QueryClient,
  body: TaskCreateRequest,
): Promise<TaskView | null> {
  try {
    const task = await tasksApi.create(body);
    acceptTask(queryClient, task);
    const view = taskViewFor(task, Date.now());
    registerUndo({
      message: `Added to ${view && view !== "open" ? TASKS_TAB_LABEL[view] : "your tasks"} · ${short(task.title)}`,
      undo: async () => {
        acceptTask(queryClient, await tasksApi.setStatus(task.id, "drop"));
      },
      doneMessage: "Removed from your list.",
    });
    return task;
  } catch (error) {
    toast(refused(error, "Couldn't add that task. Your text is still here."));
    return null;
  }
}

type StatusChange = "done" | "drop" | "snooze";

const CHANGE_WORDS: Record<StatusChange, { message: string; failed: string }> = {
  done: { message: "Done", failed: "Couldn't mark that done. It's still on your list." },
  drop: { message: "Dropped", failed: "Couldn't drop that task. It's still on your list." },
  snooze: { message: "Snoozed", failed: "Couldn't snooze that task. It's still on your list." },
};

/** Done, drop or snooze a task: it leaves the list at once; Undo reopens it. */
export async function changeTask(
  queryClient: QueryClient,
  task: Pick<TaskView, "id" | "title">,
  change: StatusChange,
  until?: string,
): Promise<boolean> {
  const rollback = removeTaskLocally(queryClient, task.id);
  const words = CHANGE_WORDS[change];
  try {
    const next =
      change === "snooze"
        ? await tasksApi.snooze(task.id, until ?? "tomorrow 9am")
        : await tasksApi.setStatus(task.id, change);
    acceptTask(queryClient, next);
    registerUndo({
      message: `${words.message} · ${short(task.title)}`,
      undo: () => reopen(queryClient, task.id),
      doneMessage: "Back on your list.",
    });
    return true;
  } catch (error) {
    rollback();
    toast(
      change === "snooze"
        ? refused(error, words.failed).replace(/^Couldn't read/, "MeeMail couldn't read")
        : words.failed,
    );
    invalidateTasks(queryClient);
    return false;
  }
}

export async function reopenTask(
  queryClient: QueryClient,
  task: Pick<TaskView, "id" | "title">,
): Promise<boolean> {
  const rollback = removeTaskLocally(queryClient, task.id);
  try {
    await reopen(queryClient, task.id);
    toast(`Back on your list · ${short(task.title)}`);
    return true;
  } catch {
    rollback();
    toast("Couldn't reopen that task just now.");
    return false;
  }
}

/**
 * `t` on a card: capture a task through the card's create_task action when it has one (it may
 * carry a suggested title), else through POST /tasks/from-card. The card gains a task_ref block;
 * Undo drops the task (the block stays, marked dropped).
 */
export async function captureTaskFromCard(
  queryClient: QueryClient,
  card: CardView,
  options: { useAction: boolean; details?: CaptureRequest },
): Promise<TaskView | null> {
  const action =
    options.useAction && !options.details
      ? card.actions.find((a) => a.behavior === "create_task")
      : undefined;
  let nextCard: CardView;
  let task: TaskView | null = null;
  try {
    if (action) {
      try {
        const result = await feedApi.action(card.id, action.id, { cardRev: card.rev });
        nextCard = result.card;
      } catch (error) {
        // An older server may not have the action wired; the capture route always is.
        if (!isApiError(error, "not_available")) throw error;
        ({ card: nextCard, task } = await tasksApi.fromCard(card.id, options.details));
      }
    } else {
      ({ card: nextCard, task } = await tasksApi.fromCard(card.id, options.details));
    }
  } catch (error) {
    toast(
      isApiError(error, "stale") || isApiError(error, "conflict")
        ? "This card changed while you were looking. Try again on the latest version."
        : isApiError(error, "not_available")
          ? "Tasks aren't wired up yet. Nothing was changed."
          : "Couldn't make a task from this card. Nothing was changed.",
    );
    invalidateBoards(queryClient);
    return null;
  }
  acceptCard(queryClient, nextCard);
  invalidateBoards(queryClient);
  const ref = task
    ? { taskId: task.id, title: task.title }
    : newTaskRef(card.blocks, nextCard.blocks);
  if (task) queryClient.setQueryData(taskKeys.task(task.id), task);
  invalidateTasks(queryClient);
  if (!ref) {
    toast("Task added.");
    return task;
  }
  // U6: a promise card's "Remind me …" settles the card; Undo then drops the task and brings the
  // card back to review.
  const settled = nextCard.status === "done" && card.status !== "done";
  registerUndo({
    message: settled ? `Reminder set · ${short(ref.title)}` : `Task added · ${short(ref.title)}`,
    undo: async () => {
      acceptTask(queryClient, await tasksApi.setStatus(ref.taskId, "drop"));
      if (settled) {
        acceptCard(queryClient, await feedApi.restore(card.id));
        invalidateBoards(queryClient);
      }
    },
    doneMessage: settled
      ? "Reminder dropped. The card is back."
      : "Task dropped. The card is unchanged.",
  });
  return task;
}

// ---------------------------------------------------------------------------------------------
// Reply tracking: both changes are held for the undo window, then sent.

function hideReply(queryClient: QueryClient, threadKey: string): () => void {
  const before = queryClient.getQueriesData<ThreadStatusView[]>({ queryKey: ["replies"] });
  queryClient.setQueriesData<ThreadStatusView[]>({ queryKey: ["replies"] }, (list) =>
    list?.filter((s) => s.threadKey !== threadKey || s.status !== "awaiting_reply"),
  );
  return () => {
    for (const [key, data] of before) queryClient.setQueryData(key, data);
  };
}

export function markReplyDone(queryClient: QueryClient, view: ThreadStatusView): void {
  let rollback = () => {};
  deferWithUndo({
    message: `No longer waiting · ${short(view.subject?.trim() || "this thread")}`,
    apply: () => {
      rollback = hideReply(queryClient, view.threadKey);
    },
    revert: () => rollback(),
    commit: async () => {
      try {
        await tasksApi.replyDone(view.threadKey);
      } catch {
        rollback();
        toast("Couldn't update that thread. It's still waiting for a reply.");
      } finally {
        invalidateReplies(queryClient);
      }
    },
    doneMessage: "Still waiting for a reply.",
  });
}

export function waitLonger(queryClient: QueryClient, view: ThreadStatusView, days = 3): void {
  let rollback = () => {};
  deferWithUndo({
    message: `Checking again in ${businessDaysLabel(days)} · ${short(view.subject?.trim() || "this thread")}`,
    apply: () => {
      const before = queryClient.getQueriesData<ThreadStatusView[]>({ queryKey: ["replies"] });
      // Show it as not due for a nudge while the change is held.
      queryClient.setQueriesData<ThreadStatusView[]>({ queryKey: ["replies"] }, (list) =>
        list?.map((s) =>
          s.threadKey === view.threadKey
            ? { ...s, nudgeDueAt: new Date(Date.now() + days * 86_400_000).toISOString() }
            : s,
        ),
      );
      rollback = () => {
        for (const [key, data] of before) queryClient.setQueryData(key, data);
      };
    },
    revert: () => rollback(),
    commit: async () => {
      try {
        await tasksApi.waitMore(view.threadKey, days);
      } catch {
        rollback();
        toast("Couldn't change that just now.");
      } finally {
        invalidateReplies(queryClient);
      }
    },
    doneMessage: "Back as it was.",
  });
}
